import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";

// Real-PDF counterpart of the template's static InvoicePreviewLayer demo: same
// header-bar + shadow-card layout, but embeds an actual backend-generated PDF.
//
// The preview itself just points the <iframe> straight at the backend URL —
// that always works, CORS or not, because CORS only restricts script-level
// access (fetch/XHR) to a response body, not navigating/embedding a URL (this
// mirrors the original sge_infantil_web PrintModal, which does exactly that).
// In the background we *also* try to fetch() the PDF into a same-origin
// blob: URL; when that succeeds (no CORS restriction on that host) we swap
// the iframe to the blob and unlock fully scripted Download/Print. When it
// fails (e.g. the CloudFront/S3 report host has no Access-Control-Allow-Origin
// header), Download/Print gracefully fall back to opening the direct URL in a
// new tab, where the browser's native PDF viewer provides its own
// download/print controls.
const PdfPreviewLayer = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { url, title, filename, backTo } = location.state || {};
  const goBack = () => (backTo ? navigate(backTo) : navigate(-1));

  const [blobUrl, setBlobUrl] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!url) return undefined;
    let objectUrl;
    fetch(url)
      .then((resp) => {
        if (!resp.ok) throw new Error("fetch failed");
        return resp.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch(() => {
        // CORS-blocked or network error — the direct <iframe src={url}>
        // already showing the document is left as-is.
      });
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  const handleDownload = async () => {
    // Prefer the native "Save As" picker (File System Access API) — this is
    // exactly what the browser's own PDF-viewer download icon does, letting
    // the user pick the folder instead of silently dropping the file into
    // the default Downloads folder.
    if (blobUrl && window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: filename || "documento.pdf",
          types: [{ description: "PDF", accept: { "application/pdf": [".pdf"] } }],
        });
        const blob = await (await fetch(blobUrl)).blob();
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return;
      } catch (err) {
        if (err?.name === "AbortError") return; // user cancelled the picker
        // fall through to the plain-link fallback below
      }
    }
    if (blobUrl) {
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || "documento.pdf";
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }
    // No blob — the background fetch() was CORS-blocked (the PDF host has no
    // Access-Control-Allow-Origin header), so the browser will never let our
    // JS read those bytes to drive a scripted save. Opening the direct URL is
    // the only thing left we can do; the browser's own PDF viewer that opens
    // has its own download icon in its toolbar, which does work (it downloads
    // via the browser's networking stack, not page JS, so CORS doesn't apply
    // to it).
    toast.info(t("documento.corsDownloadHint"));
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handlePrint = () => {
    // blob: URLs are same-origin to our document, so contentWindow.print() is
    // allowed; a cross-origin iframe would throw a SecurityError here.
    if (blobUrl && iframeRef.current) {
      try {
        iframeRef.current.contentWindow.focus();
        iframeRef.current.contentWindow.print();
        return;
      } catch (err) {
        // fall through to the external-tab fallback below
      }
    }
    toast.info(t("documento.corsPrintHint"));
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!url) {
    return (
      <div className='card'>
        <div className='card-body text-center py-64'>
          <p className='text-secondary-light mb-16'>{t("documento.notFound")}</p>
          <button type='button' className='btn btn-primary-600' onClick={goBack}>
            <i className='ri-arrow-left-line' /> {t("global.back")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='card'>
      <ToastContainer />
      <div className='card-header'>
        <div className='d-flex flex-wrap align-items-center justify-content-between gap-2'>
          <h6 className='mb-0 text-md'>{title || t("documento.title")}</h6>
          <div className='d-flex flex-wrap align-items-center gap-2'>
            <button
              type='button'
              className='btn btn-sm btn-outline-secondary radius-8 d-inline-flex align-items-center gap-1'
              onClick={goBack}
            >
              <Icon icon='solar:arrow-left-linear' className='text-xl' />
              {t("global.back")}
            </button>
            <button
              type='button'
              className='btn btn-sm btn-warning radius-8 d-inline-flex align-items-center gap-1'
              onClick={handleDownload}
            >
              <Icon icon='solar:download-linear' className='text-xl' />
              {t("documento.download")}
            </button>
            <button
              type='button'
              className='btn btn-sm btn-danger radius-8 d-inline-flex align-items-center gap-1'
              onClick={handlePrint}
            >
              <Icon icon='basil:printer-outline' className='text-xl' />
              {t("documento.print")}
            </button>
          </div>
        </div>
      </div>
      <div className='card-body py-40'>
        <div className='row justify-content-center'>
          <div className='col-lg-10'>
            <div className='shadow-4 border radius-8 overflow-hidden' style={{ minHeight: "80vh" }}>
              <iframe
                ref={iframeRef}
                src={blobUrl || url}
                title={title || "PDF"}
                style={{ width: "100%", height: "80vh", border: "none" }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfPreviewLayer;
